// src/hooks/useRegistrations.ts
import { useState, useEffect, useMemo } from 'react';
import axios from "../utils/axiosConfig";
//import { handleApiError } from '../utils/apiErrorHandler';

interface Athlete {
    _id: string;
    firstname: string;
    surname: string;
    gender: string;
    age: number;
    club: string;
}

interface RegistrationGroup {
    competitionGroup: string;
    type: string;
    count: number;
    athletes: Athlete[];
}

export const useRegistrations = (competitionId: string, options?: {
    sortByCompetitionGroup?: boolean;
    groupByType?: boolean;
}) => {
    const [registrations, setRegistrations] = useState<RegistrationGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Default options
    const {
        sortByCompetitionGroup = false,
        groupByType = false
    } = options || {};

    useEffect(() => {
        const fetchRegistrations = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/competition-registrations/${competitionId}/registrations-summary`,
                    { withCredentials: true }
                );
                setRegistrations(response.data);
                setError(null);
            } catch (error) {
                //handleApiError(error, "Fehler beim Laden der Wettkampfdaten.");
                setError("Fehler beim Laden der Wettkampfdaten");
            } finally {
                setLoading(false);
            }
        };

        if (competitionId) {
            fetchRegistrations();
        }
    }, [competitionId]);

    // Processed registrations based on sorting/grouping options
    const processedRegistrations = useMemo(() => {
        let result = [...registrations];
        
        // Sort by competition group if requested
        if (sortByCompetitionGroup) {
            result.sort((a, b) => {
                return a.competitionGroup.localeCompare(b.competitionGroup);
            });
        }
        
        // Further grouping by type (Elite/Basis) if requested
        if (groupByType) {
            // First group by type
            const groupedByType: Record<string, RegistrationGroup[]> = {};
            
            result.forEach(reg => {
                if (!groupedByType[reg.type]) {
                    groupedByType[reg.type] = [];
                }
                groupedByType[reg.type].push(reg);
            });
            
            // Then flatten back to array, with "Elite" types coming first
            result = Object.keys(groupedByType)
                .sort((a, b) => {
                    // Put "Elite" first, then alphabetical
                    if (a === "Elite") return -1;
                    if (b === "Elite") return 1;
                    return a.localeCompare(b);
                })
                .flatMap(type => groupedByType[type]);
        }
        
        return result;
    }, [registrations, sortByCompetitionGroup, groupByType]);

    return { 
        registrations: processedRegistrations, 
        rawRegistrations: registrations, // Original unsorted data
        loading, 
        error 
    };
};